import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Calendar, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomButton from "../../../../../custom/custom-button/CustomButton";
import CustomDatePicker from "../../../../../shared/date-picker/Date/CustomDatePicker";
import { Aviation } from "../../../../../../services";
import {
  buildAviationRouteDatesPayload,
  createAviationRouteDateRows,
  getPreviousFilledDate,
  validateAviationRouteDates,
} from "./aviationRouteDates";
import style from "./EditAviationRouteDatesModal.module.scss";

const TYPE_TRANSLATIONS = {
  Loading: {
    point: "orderInfoClient.editRouteDatesModal.loadingPoint",
    dateLabel: "orderInfoClient.editRouteDatesModal.loadingDate",
  },
  Unloading: {
    point: "orderInfoClient.editRouteDatesModal.unloadingPoint",
    dateLabel: "orderInfoClient.editRouteDatesModal.unloadingDate",
  },
};

const EditAviationRouteDatesModal = ({ isOpen, order, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setRows(createAviationRouteDateRows(order));
    setErrors({});
    setIsLoading(false);
  }, [isOpen, order]);

  if (!isOpen) return null;

  const changeDate = (id, date) => {
    setRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== id || (!date && row.date)) return row;

        return { ...row, date };
      }),
    );
    setErrors((currentErrors) => {
      if (!currentErrors[id]) return currentErrors;

      const nextErrors = { ...currentErrors };
      delete nextErrors[id];
      return nextErrors;
    });
  };

  const handleConfirm = async () => {
    const nextErrors = validateAviationRouteDates(rows);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    const { orderId, shippingDate, unloadingDate } = buildAviationRouteDatesPayload(
      order?.id,
      rows,
    );

    try {
      setIsLoading(true);
      await Aviation.updateShippingDate(orderId, { shippingDate, unloadingDate });
      toast.success(t("orderInfoClient.editRouteDatesModal.updateSuccess"));
      onSuccess?.();
      onCancel?.();
    } catch (error) {
      console.error("Error updating aviation route dates:", error);
      toast.error(t("orderInfoClient.editRouteDatesModal.updateError"));
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className={style.overlay}
      data-portal-modal="true"
      onMouseDown={(event) => {
        if (!isLoading && event.target === event.currentTarget) onCancel?.();
      }}
    >
      <div
        className={style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-aviation-route-dates-title"
      >
        <button
          type="button"
          className={style.closeButton}
          onClick={onCancel}
          disabled={isLoading}
          aria-label={t("orderInfoClient.editRouteDatesModal.close")}
        >
          <X size={26} strokeWidth={1.6} />
        </button>

        <header className={style.header}>
          <h2 id="edit-aviation-route-dates-title" className={style.title}>
            {t("orderInfoClient.editRouteDatesModal.title")}
          </h2>
          <p className={style.description}>
            {t("orderInfoClient.editRouteDatesModal.description")}
          </p>
        </header>

        <div className={style.routeList}>
          {rows.map((row, index) => {
            const typeTranslation = TYPE_TRANSLATIONS[row.type];
            const minDate = getPreviousFilledDate(rows, index);

            return (
              <div className={style.routeRow} key={row.id}>
                <div className={style.pointInfo}>
                  <span className={style.routeRail} aria-hidden="true" />
                  <div>
                    <h3 className={style.pointTitle}>{t(typeTranslation.point)}</h3>
                    <div className={style.pointMeta}>
                      <span className={style.city}>{row.cityName || row.address}</span>
                    </div>
                  </div>
                </div>

                <div className={style.dateField}>
                  <label className={style.dateLabel}>{t(typeTranslation.dateLabel)}</label>
                  <div className={style.inputWithIcon}>
                    <div className={style.datePickerWrapper}>
                      <CustomDatePicker
                        selected={row.date}
                        onChange={(date) => changeDate(row.id, date)}
                        onKeyDown={(event) => {
                          if (row.date && ["Backspace", "Delete"].includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        minDate={minDate || undefined}
                        dateFormat="dd.MM.yyyy"
                        placeholderText="ДД.ММ.ГГГГ"
                        popperPlacement="bottom-start"
                        disabled={isLoading}
                      />
                    </div>
                    <Calendar className={style.inputIcon} size={20} />
                  </div>
                  {errors[row.id] && (
                    <span className={style.errorText} role="alert">
                      {t(`orderInfoClient.editRouteDatesModal.validation.${errors[row.id]}`)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={style.actions}>
          <CustomButton
            isWhite
            isWhiteBox
            text={t("orderInfoClient.editRouteDatesModal.cancel")}
            onClick={onCancel}
            disabled={isLoading}
          />
          <CustomButton
            isNavy
            text={t("orderInfoClient.editRouteDatesModal.confirm")}
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EditAviationRouteDatesModal;
